/**
 * AllocationSettingsForm - Edit allocation display settings
 *
 * Fields: custom_label, show_label, color_override
 * Used for customizing how an aircraft appears on the ramp view.
 */

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { updateAllocation } from '@/lib/api/allocations'
import { broadcastInvalidation } from '@/lib/sync'

interface AllocationSettingsFormProps {
  allocationId: string
  initialData: {
    customLabel: string | null
    showLabel: boolean
    colorOverride: string | null
  }
  prefocusField?: string
  onSuccess?: () => void
  onCancel?: () => void
}

interface FormValues {
  custom_label: string
  show_label: boolean
  color_override: string
}

// Preset colors for quick selection
const PRESET_COLORS = [
  { value: '', label: 'Default', color: '#94a3b8' },
  { value: '#ef4444', label: 'Red', color: '#ef4444' },
  { value: '#f97316', label: 'Orange', color: '#f97316' },
  { value: '#eab308', label: 'Yellow', color: '#eab308' },
  { value: '#22c55e', label: 'Green', color: '#22c55e' },
  { value: '#3b82f6', label: 'Blue', color: '#3b82f6' },
  { value: '#8b5cf6', label: 'Purple', color: '#8b5cf6' },
  { value: '#ec4899', label: 'Pink', color: '#ec4899' },
]

export function AllocationSettingsForm({
  allocationId,
  initialData,
  prefocusField,
  onSuccess,
  onCancel,
}: AllocationSettingsFormProps) {
  const queryClient = useQueryClient()

  const form = useForm<FormValues>({
    defaultValues: {
      custom_label: initialData.customLabel || '',
      show_label: initialData.showLabel,
      color_override: initialData.colorOverride || '',
    },
  })

  // Auto-focus the specified field
  useEffect(() => {
    if (prefocusField) {
      const fieldMap: Record<string, keyof FormValues> = {
        label: 'custom_label',
        customLabel: 'custom_label',
        color: 'color_override',
        colorOverride: 'color_override',
      }
      const fieldName = fieldMap[prefocusField]
      if (fieldName) {
        setTimeout(() => form.setFocus(fieldName), 100)
      }
    }
  }, [prefocusField, form])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      return updateAllocation(allocationId, {
        custom_label: values.custom_label || null,
        show_label: values.show_label,
        color_override: values.color_override || null,
      })
    },
    onSuccess: () => {
      toast.success('Settings updated')
      queryClient.invalidateQueries({ queryKey: ['allocations'] })
      broadcastInvalidation([['allocations']])
      onSuccess?.()
    },
    onError: (error) => {
      toast.error('Failed to update: ' + (error as Error).message)
    },
  })

  const onSubmit = (values: FormValues) => {
    mutation.mutate(values)
  }

  const selectedColor = form.watch('color_override')

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      {/* Custom Label */}
      <div className="space-y-1.5">
        <Label htmlFor="custom_label">Custom Label</Label>
        <Input
          id="custom_label"
          placeholder="e.g., VIP, Maintenance, Reserved"
          {...form.register('custom_label')}
        />
        <p className="text-xs text-muted-foreground">
          Override the default tail number display with a custom label
        </p>
      </div>

      {/* Show Label Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Show Label</Label>
          <p className="text-xs text-muted-foreground">
            Display label on the ramp view
          </p>
        </div>
        <Switch
          checked={form.watch('show_label')}
          onCheckedChange={(checked) => form.setValue('show_label', checked)}
        />
      </div>

      {/* Color Override */}
      <div className="space-y-2">
        <Label>Color Override</Label>
        <div className="flex flex-wrap gap-2">
          {PRESET_COLORS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => form.setValue('color_override', preset.value)}
              className={`
                w-8 h-8 rounded-full border-2 transition-all
                ${
                  selectedColor === preset.value
                    ? 'border-foreground scale-110'
                    : 'border-transparent hover:border-muted-foreground/50'
                }
              `}
              style={{ backgroundColor: preset.color }}
              title={preset.label}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Label htmlFor="color_override" className="text-xs text-muted-foreground shrink-0">
            Or custom:
          </Label>
          <Input
            id="color_override"
            type="text"
            placeholder="#ff0000"
            className="h-8 text-sm"
            {...form.register('color_override')}
          />
          {selectedColor && (
            <div
              className="w-8 h-8 rounded border shrink-0"
              style={{ backgroundColor: selectedColor }}
            />
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}
